import {
  HighnotePaymentCard,
  HNAmountLimitSpendRule,
  HNCardFormFactor,
  HNCVVSpendRule,
  HNPaymentCard,
  HNPaymentCardShippingMethod,
  HNStreetAddressSpendRule,
  MerchantSpendRule,
} from 'API';
import * as yup from 'yup';
import { ListType } from 'app/pages/cards/spend-rules/utils';

export const NO_GROUP = 'NO_GROUP';
export const NO_RULE = 'NO_RULE';
export const NO_SHIPPING = 'NO_SHIPPING';

export enum FormType {
  OrderPhysical = 'ORDER_PHYSICAL',
  IssueCard = 'ISSUE_CARD',
  UpdateCard = 'UPDATE_CARD',
}

export const CARD_TYPES: Record<HNCardFormFactor, string> = {
  [HNCardFormFactor.PHYSICAL]: 'Physical card',
  [HNCardFormFactor.VIRTUAL]: 'Virtual card',
};

const USPS_SHIPPING_OPTIONS: { [K in HNPaymentCardShippingMethod]?: string } = {
  [HNPaymentCardShippingMethod.USPS_GROUND]: '(USPS) 2-8 business days',
  [HNPaymentCardShippingMethod.USPS_PRIORITY]: '(USPS) 1-3 business days',
  [HNPaymentCardShippingMethod.USPS_EXPRESS]: '(USPS) Next business day-2 days',
};

const UPS_SHIPPING_OPTIONS: { [K in HNPaymentCardShippingMethod]?: string } = {
  [HNPaymentCardShippingMethod.UPS_GROUND]: '(UPS) 1-5 business days',
  [HNPaymentCardShippingMethod.UPS_SECOND_DAY]: '(UPS) 2 business days',
  [HNPaymentCardShippingMethod.UPS_NEXT_DAY]: '(UPS) Next business day',
};

const FEDEX_SHIPPING_OPTIONS: { [K in HNPaymentCardShippingMethod]?: string } = {
  [HNPaymentCardShippingMethod.FEDEX_GROUND]: '(FedEx) 1-5 business days',
  [HNPaymentCardShippingMethod.FEDEX_2_DAY]: '(FedEx) 2 business days',
  // SEE-629: Added to match previous UPS_NEXT_DAY overnight option
  [HNPaymentCardShippingMethod.FEDEX_PRIORITY_OVERNIGHT]: '(FedEx) Next business day',
};

/** Combined map of all shipping methods (UPS + FedEx + USPS) for display/lookup of historical and new shipments */
export const SHIPPING_METHODS: { [K in HNPaymentCardShippingMethod]?: string } = {
  ...USPS_SHIPPING_OPTIONS,
  ...UPS_SHIPPING_OPTIONS,
  ...FEDEX_SHIPPING_OPTIONS,
};

/** Returns the active shipping options based on the useFedEx feature flag.
 *  When `signatureRequiredSuffix` is provided, it's appended to USPS Priority Mail
 *  Express's label (SEE-472). The caller passes the already-translated string.
 */
export const getActiveShippingOptions = (
  useFedEx: boolean,
  signatureRequiredSuffix?: string
): { [K in HNPaymentCardShippingMethod]?: string } => {
  const usps = signatureRequiredSuffix
    ? {
        ...USPS_SHIPPING_OPTIONS,
        [HNPaymentCardShippingMethod.USPS_EXPRESS]: `${
          USPS_SHIPPING_OPTIONS[HNPaymentCardShippingMethod.USPS_EXPRESS]
        } ${signatureRequiredSuffix}`,
      }
    : USPS_SHIPPING_OPTIONS;
  return {
    ...usps,
    ...(useFedEx ? FEDEX_SHIPPING_OPTIONS : UPS_SHIPPING_OPTIONS),
  };
};

/** USPS-only shipping options with an optional signature-required suffix. */
export const getUspsOnlyShippingOptions = (
  signatureRequiredSuffix?: string
): { [K in HNPaymentCardShippingMethod]?: string } =>
  signatureRequiredSuffix
    ? {
        ...USPS_SHIPPING_OPTIONS,
        [HNPaymentCardShippingMethod.USPS_EXPRESS]: `${
          USPS_SHIPPING_OPTIONS[HNPaymentCardShippingMethod.USPS_EXPRESS]
        } ${signatureRequiredSuffix}`,
      }
    : { ...USPS_SHIPPING_OPTIONS };

/** Returns true if the given shipping-method key is a USPS method. */
export const isUspsShippingMethod = (method: string | null | undefined): boolean =>
  method != null && method in USPS_SHIPPING_OPTIONS;

export const schemaValidationMessages = {
  cardName: `Must be between 4 and 23 characters`,
  project: 'You must select a project',
  cardHolder: {
    required: 'You must select a cardholder',
    email: 'Cardholder must have an email',
  },
};

export const schema = yup.object({
  formType: yup.mixed<FormType>(),
  cardName: yup
    .string()
    .required()
    .when(['cardType', 'formType'], {
      is: (cardType: string, formType: FormType) =>
        formType === FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
      then: (schema) => schema,
      otherwise: (schema) =>
        schema
          .test('custom', schemaValidationMessages.cardName, (value) => {
            return value !== undefined && value.length >= 4 && value.length <= 23;
          })
          .required(schemaValidationMessages.cardName),
    }),

  cardHolder: yup
    .string()
    .email(schemaValidationMessages.cardHolder.email)
    .required(schemaValidationMessages.cardHolder.required),
  isConstructionType: yup.bool(),
  isAgaveClient: yup.bool(),
  cardGroupId: yup.string(),
  cardType: yup.string().required(),
  cardExpirationDate: yup.date().typeError('').required(''),

  addressFirstName: yup.string().when(['cardType', 'formType'], {
    is: (cardType: string, formType: FormType) =>
      formType !== FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema,
  }),
  addressLastName: yup.string().when(['cardType', 'formType'], {
    is: (cardType: string, formType: FormType) =>
      formType !== FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema,
  }),
  addressZip: yup.string().when(['cardType', 'formType'], {
    is: (cardType: string, formType: FormType) =>
      formType !== FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema,
  }),
  addressStreet1: yup.string().when(['cardType', 'formType'], {
    is: (cardType: string, formType: FormType) =>
      formType !== FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema,
  }),
  addressStreet2: yup.string(),
  addressCity: yup.string().when(['cardType', 'formType'], {
    is: (cardType: string, formType: FormType) =>
      formType !== FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema,
  }),
  addressState: yup.string().when(['cardType', 'formType'], {
    is: (cardType: string, formType: FormType) =>
      formType !== FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema,
  }),
  requireSignature: yup.bool().required(),
  shippingMethod: yup
    .mixed<typeof NO_SHIPPING | HNPaymentCardShippingMethod>()
    .when(['cardType', 'formType'], {
      is: (cardType: string, formType: FormType) =>
        formType !== FormType.UpdateCard && cardType === HNCardFormFactor.PHYSICAL,
      then: (schema) => schema.oneOf(Object.values(HNPaymentCardShippingMethod)).required(),
      otherwise: (schema) => schema,
    }),
  isMonthlyBudgetUnlimited: yup.bool().required(),
  isMaximumTransactionAmountUnlimited: yup.bool().required(),

  monthlySpendLimit: yup
    .number()
    .min(0, 'Monthly spend limit must be 0 or higher')
    .nullable()
    .when('isMonthlyBudgetUnlimited', {
      is: true,
      then: (schema) => schema.transform(() => undefined).notRequired(),
      otherwise: (schema) =>
        schema
          .required('Monthly spend limit is required when not unlimited')
          .transform((value, originalValue) => (originalValue === '' ? undefined : value)),
    }),

  maximumTransactionAmount: yup
    .number()
    .min(0, 'Maximum transaction amount must be 0 or higher')
    .nullable()
    .when('isMaximumTransactionAmountUnlimited', {
      is: true,
      then: (schema) => schema.transform(() => undefined).notRequired(),
      otherwise: (schema) =>
        schema
          .required('Maximum transaction amount must be 0 or higher')
          .transform((value, originalValue) => (originalValue === '' ? undefined : value)),
    }),
  merchantSpendRuleId: yup.string(),
  spendingByCategoryType: yup.mixed<ListType>().oneOf(Object.values(ListType)).optional(),
  spendingByCategory: yup.array().of(yup.string()).optional(),
  spendingByCountryType: yup.mixed<ListType>().oneOf(Object.values(ListType)).optional(),
  spendingByCountry: yup.array().of(yup.string()).optional(),
  requireCardCode: yup.bool().required(),
  requireAddress: yup.bool().required(),
});

export type NewCardFormValues = yup.InferType<typeof schema>;

export interface SelectedPaymentCardProps {
  card: HNPaymentCard;
  details?: HighnotePaymentCard;
}

export const getAttachedAmountSpendRuleFromPaymentCard = (paymentCard?: SelectedPaymentCardProps) => {
  const filteredRules =
    paymentCard?.card.attachedSpendRules?.edges?.filter(
      (spendRule) => (spendRule.node as any)?.maximumAmount != null
    ) || [];

  return filteredRules.length > 0
    ? (filteredRules.at(0)?.node as unknown as HNAmountLimitSpendRule)
    : undefined;
};

export const getAttachedAmountVelocitySpendRuleFromPaymentCard = (paymentCard?: SelectedPaymentCardProps) => {
  const filteredRules =
    paymentCard?.card.attachedVelocityRules?.edges?.filter(
      (velocityRule) => (velocityRule.node?.cumulativeRule as any)?.maximumAmount != null
    ) || [];

  return filteredRules.at(0)?.node ?? undefined;
};

export const getAttachedCVVSpendRuleFromPaymentCard = (paymentCard?: SelectedPaymentCardProps) => {
  const filteredRules =
    paymentCard?.card.attachedSpendRules?.edges?.filter(
      (spendRule) =>
        (spendRule.node as any)?.CVVAllowed?.length > 0 || (spendRule.node as any)?.CVVBlocked?.length > 0
    ) || [];

  return filteredRules.length > 0 ? (filteredRules.at(0)?.node as unknown as HNCVVSpendRule) : undefined;
};

export const getAttachedStreetAddressSpendRuleFromPaymentCard = (paymentCard?: SelectedPaymentCardProps) => {
  const filteredRules =
    paymentCard?.card.attachedSpendRules?.edges?.filter(
      (spendRule) =>
        (spendRule.node as any)?.streetAddressAllowed?.length > 0 ||
        (spendRule.node as any)?.streetAddressBlocked?.length > 0
    ) || [];

  return filteredRules.length > 0
    ? (filteredRules.at(0)?.node as unknown as HNStreetAddressSpendRule)
    : undefined;
};

export const getAttachedMerchantSpendRuleFromPaymentCard = (
  rules: MerchantSpendRule[],
  paymentCard?: SelectedPaymentCardProps
) =>
  paymentCard?.card.id
    ? rules.find((rule) => rule.paymentCards && rule.paymentCards.includes(paymentCard?.card.id))
    : undefined;
